/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { uploadFile } from "./upload.service";

import { CREATE_TEMPLATE_COSTUME_ATTRIBUTE } from "../graphql/createAttribute.mutation";

import { UPDATE_TEMPLATE_COSTUME_ATTRIBUTE } from "../graphql/updateAttribute.mutation";

import { DELETE_TEMPLATE_COSTUME_ATTRIBUTE } from "../graphql/deleteAttribute.mutation";

const log: any = LoggerModule?.getInstance();

export interface AttributeFormData {
  name?: string;
  desc?: string;
  description?: string;
  dynaInventory?: string | number;
  isSingleVal?: boolean;
  isVisible?: boolean;
  isMandatory?: boolean;
  isDynamic?: boolean;
  isLabel?: boolean;
  images?: any[];
  costumeFeature?: string;
  [key: string]: any;
}

export interface CreateAttributeProps {
  formData?: AttributeFormData;
  [key: string]: any;
}

export interface UpdateAttributeProps {
  id: string;
  formData?: AttributeFormData;
  [key: string]: any;
}

export interface DeleteAttributeProps {
  id: string;
}

export interface IAttributeMethods extends IPluginMethodMap {
  createAttribute(args: CreateAttributeProps): Promise<any>;
  updateAttribute(args: UpdateAttributeProps): Promise<any>;
  deleteAttribute(args: DeleteAttributeProps): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const buildAttributePayload = async (args: any, mode: "create" | "update") => {
  const raw = args?.formData ?? args ?? {};
  const name = raw?.name ?? "Untitled Attribute";
  const uploadedImages = await uploadFile(raw?.images, name);

  const payload: any = {
    costumeFeature: raw?.costumeFeature ?? raw?.template_costumeFeature,
    name,
    dynaInventory: raw?.dynaInventory ? Number(raw?.dynaInventory) : undefined,
    isSingleVal: raw?.isSingleVal === true,
    desc: raw?.desc ?? raw?.description,
    isVisible: raw?.isVisible === true,
    isMandatory: raw?.isMandatory === true,
    isDynamic: raw?.isDynamic === true,
    isLabel: raw?.isLabel === true,
    images: uploadedImages,
  };

  if (mode === "create") {
    payload.presentation = raw?.presentation;
  }

  if (mode === "update") {
    delete payload.presentation;
    delete payload.costumeFeature;
  }

  return payload;
};

const getDisplayMessage = (err: any) => {
  const displayMessage =
    err?.graphQLErrors &&
    Array.isArray(err?.graphQLErrors) &&
    err?.graphQLErrors.find((item: any) => item.extensions);

  return (
    displayMessage?.extensions?.display_message ||
    err?.message ||
    "Something went wrong"
  );
};

export class AttributeService implements IAgentPlugin {
  readonly methods: IAttributeMethods;

  constructor() {
    this.methods = {
      createAttribute: this.createAttribute.bind(this),
      updateAttribute: this.updateAttribute.bind(this),
      deleteAttribute: this.deleteAttribute.bind(this),
    };
  }

  async createAttribute(args: CreateAttributeProps) {
    log?.blue?.("AttributeService.createAttribute:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (!client) {
        throw new Error("GraphQL client not found");
      }

      const data = await buildAttributePayload(args, "create");

      const res = await client.mutate({
        mutation: CREATE_TEMPLATE_COSTUME_ATTRIBUTE,
        variables: {
          data,
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.createCostumeAttribute;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }

  async updateAttribute(args: UpdateAttributeProps) {
    log?.blue?.("AttributeService.updateAttribute:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (!client) {
        throw new Error("GraphQL client not found");
      }

      if (!args?.id) {
        throw new Error("ID not found for update");
      }

      const update = await buildAttributePayload(args, "update");

      const res = await client.mutate({
        mutation: UPDATE_TEMPLATE_COSTUME_ATTRIBUTE,
        variables: {
          filter: {
            _id: args.id,
          },
          update,
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.updateCostumeAttribute;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }

  async deleteAttribute(args: DeleteAttributeProps) {
    log?.blue?.("AttributeService.deleteAttribute:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (!client) {
        throw new Error("GraphQL client not found");
      }

      if (!args?.id) {
        throw new Error("Missing ID for delete");
      }

      const res = await client.mutate({
        mutation: DELETE_TEMPLATE_COSTUME_ATTRIBUTE,
        variables: {
          filter: {
            _id: args.id,
          },
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.deleteCostumeAttribute;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }
}
